import { ParamsInput, ParamValue, TranslationType } from "../types/Types";
import enTranslations from "../resources/en.json";
import arTranslations from "../resources/ar.json";

type TextDirection = "ltr" | "rtl";

class TranslateService {
  private static defaultLanguage: string = "en";
  private static cache: Record<string, string> = {};

  private static translationsMap: Record<string, TranslationType | unknown> = {
    en: enTranslations,
    ar: arTranslations,
  };

  // Map languages to their default directions
  private static directionsMap: Record<string, TextDirection> = {
    en: "ltr",
    ar: "rtl",
  };

  private static DIR_KEY = "dir"; // localStorage key for manual override

  // ------- Direction API -------
  static async getCurrentDirection(lang?: string): Promise<TextDirection> {
    const stored = localStorage.getItem(this.DIR_KEY);
    if (stored === "ltr" || stored === "rtl") return stored as TextDirection;

    const effectiveLang = lang ?? (await this.getCurrentLanguage());
    return this.getDirectionSync(effectiveLang);
  }

  static getDirectionSync(lang?: string): TextDirection {
    const stored =
      typeof window !== "undefined" ? localStorage.getItem(this.DIR_KEY) : null;
    if (stored === "ltr" || stored === "rtl") return stored as TextDirection;

    const l = (lang ?? this.defaultLanguage).toLowerCase();
    return this.directionsMap[l] ?? "ltr";
  }

  static async setDirection(dir: TextDirection): Promise<void> {
    localStorage.setItem(this.DIR_KEY, dir);
    this.clearCache(); // not required for text, but safe if templates depend on dir
  }

  static clearDirectionOverride(): void {
    localStorage.removeItem(this.DIR_KEY);
    this.clearCache();
  }
  // -----------------------------

  static async translate(i18n: string, params?: ParamsInput): Promise<string> {
    if (!i18n) return i18n;

    const lang: string = await this.getCurrentLanguage();
    const cacheKey: string = this.buildCacheKey(lang, i18n, params);
    const cached: string | undefined = this.cache[cacheKey];
    if (typeof cached === "string") return cached;

    const raw: string | undefined =
      this.getRawByPath(i18n, lang) ??
      this.getRawByPath(i18n, this.defaultLanguage);

    const result: string =
      typeof raw === "string" ? this.applyParams(raw, params) : i18n;

    this.cache[cacheKey] = result;
    return result;
  }

  static translateSync(i18n: string, params?: any, lang?: string): string {
    const useLang = lang || this.defaultLanguage;
    const raw =
      this.getRawByPath(i18n, useLang) ??
      this.getRawByPath(i18n, this.defaultLanguage);
    return raw ? this.applyParams(raw, params) : i18n;
  }

  static async exists(i18n: string): Promise<boolean> {
    const lang: string = await this.getCurrentLanguage();
    return (
      this.getRawByPath(i18n, lang) !== undefined ||
      this.getRawByPath(i18n, this.defaultLanguage) !== undefined
    );
  }

  static async getCurrentLanguage(): Promise<string> {
    const lang: string = localStorage.getItem("lang") ?? "";
    return lang === "" ? this.defaultLanguage : lang;
  }

  static async setCurrentLanguage(lang: string): Promise<void> {
    localStorage.setItem("lang", lang);
    this.clearCache();
    // If direction override is not set, auto-follow language
    const hasOverride = localStorage.getItem(this.DIR_KEY);
    if (!hasOverride) {
      const autoDir = this.getDirectionSync(lang);
      localStorage.setItem(this.DIR_KEY, autoDir);
    }
  }

  static preloadLanguage(lang: string, payload: TranslationType): void {
    const existing: any = this.translationsMap[lang] ?? {};
    this.translationsMap[lang] = this.deepMerge(existing, payload);
    this.clearCache();
  }

  static clearCache(): void {
    this.cache = {};
  }

  private static buildCacheKey(
    lang: string,
    i18n: string,
    params?: ParamsInput
  ): string {
    return lang + "::" + i18n + "::" + this.paramsKey(params);
  }

  private static paramsKey(params?: ParamsInput): string {
    if (params === undefined) return "";
    if (Array.isArray(params)) return JSON.stringify(params);

    const normalized: Record<string, ParamValue> = {};
    const keys: string[] = Object.keys(params);
    let i: number;

    for (i = 0; i < keys.length; i++) {
      const k: string = keys[i];
      const v: ParamValue = (params as Record<string, ParamValue>)[k];
      if (/^\d+$/.test(k)) normalized["arg" + k] = v;
      else normalized[k] = v;
    }

    const ordered: Array<[string, ParamValue]> = Object.entries(
      normalized
    ).sort(function (a: any[], b: any[]) {
      return a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0;
    });
    return JSON.stringify(ordered);
  }

  private static getRawByPath(i18n: string, lang: string): string | undefined {
    const root: unknown = this.translationsMap[lang];
    if (!root || typeof i18n !== "string") return undefined;

    const segments: string[] = i18n.split(":").filter(function (s) {
      return s.length > 0;
    });
    let node: any = root;
    let idx: number;

    for (idx = 0; idx < segments.length; idx++) {
      const seg: string = segments[idx];
      if (node && typeof node === "object" && seg in node) node = node[seg];
      else return undefined;
    }
    return typeof node === "string" ? node : undefined;
  }

  private static applyParams(template: string, params?: ParamsInput): string {
    if (!params) return template;

    const map: Record<string, ParamValue> = {};
    let i: number;

    if (Array.isArray(params)) {
      for (i = 0; i < params.length; i++) map["arg" + i] = params[i];
    } else {
      const keys: string[] = Object.keys(params);
      for (i = 0; i < keys.length; i++) {
        const k: string = keys[i];
        const v: ParamValue = (params as Record<string, ParamValue>)[k];
        map[/^\d+$/.test(k) ? "arg" + k : k] = v;
      }
    }

    return template.replace(
      /\{(arg\d+)\}/g,
      function (_m: string, group: string): string {
        return Object.prototype.hasOwnProperty.call(map, group)
          ? String(map[group])
          : "{" + group + "}";
      }
    );
  }

  private static deepMerge(target: any, source: any): any {
    if (target === source) return target;
    if (typeof source !== "object" || source === null) return source;

    const out: any = Array.isArray(target) ? target.slice() : { ...target };
    const keys: string[] = Object.keys(source);
    let i: number;

    for (i = 0; i < keys.length; i++) {
      const k: string = keys[i];
      const sv: any = source[k];
      const tv: any = out[k];

      if (sv && typeof sv === "object" && !Array.isArray(sv)) {
        out[k] = this.deepMerge(tv && typeof tv === "object" ? tv : {}, sv);
      } else {
        out[k] = sv;
      }
    }
    return out;
  }
}

export default TranslateService;
export type { ParamsInput, ParamValue, TextDirection };
